import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, Circle, Sparkles, X, ArrowUpRight } from 'lucide-react';

const LS_KEY = 'nexus_onboard_freemium';

/**
 * Lightweight onboarding checklist shown only to Freemium tenants on the
 * Dashboard. Completion is derived from real data (logo, first image, form)
 * plus action steps the user ticks off. Dismissible (persisted in localStorage).
 */
const FreemiumOnboarding = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`${LS_KEY}_dismissed`) === '1');
  const [clicked, setClicked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${LS_KEY}_clicked`) || '{}'); } catch { return {}; }
  });

  useEffect(() => {
    (async () => {
      try {
        const [usageRes, brandingRes] = await Promise.all([
          api.get('/tenant/usage').catch(() => ({ data: {} })),
          api.get('/tenant/branding').catch(() => ({ data: {} })),
        ]);
        setState({ usage: usageRes.data, branding: brandingRes.data });
      } catch {
        setState({ usage: {}, branding: {} });
      }
    })();
  }, []);

  if (dismissed || !state) return null;
  const planLevel = state.usage?.plan_level;
  if (planLevel !== 'freemium' && planLevel !== 'free') return null;

  const markClicked = (key, path) => {
    const next = { ...clicked, [key]: true };
    setClicked(next);
    localStorage.setItem(`${LS_KEY}_clicked`, JSON.stringify(next));
    if (path) navigate(path);
  };

  const hasLogo = !!(state.branding?.logo_url);
  const hasMedia = (state.usage?.usage?.assets?.current || 0) > 0
    || (state.usage?.usage?.storage?.used_mb || 0) > 0;
  const hasForm = (state.usage?.usage?.forms?.current || 0) > 0;

  const steps = [
    { key: 'landing', label: 'Personalize your landing page', desc: 'Add your headline, colors and content.', done: !!clicked.landing, action: () => markClicked('landing', '/landing-page'), cta: 'Customize' },
    { key: 'logo', label: 'Add your logo & favicon', desc: 'Make the site feel like yours.', done: hasLogo, action: () => markClicked('logo', '/theme-settings'), cta: 'Add logo' },
    { key: 'media', label: 'Upload your first image', desc: 'Build your media library.', done: hasMedia, action: () => markClicked('media', '/media'), cta: 'Upload' },
    { key: 'form', label: 'Create a contact form', desc: 'Start capturing leads.', done: hasForm, action: () => markClicked('form', '/forms'), cta: 'Create form' },
    { key: 'golive', label: 'Publish & go live', desc: 'Request to launch your site.', done: !!clicked.golive, action: () => markClicked('golive', null), cta: 'How?' },
  ];

  const completed = steps.filter((s) => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(`${LS_KEY}_dismissed`, '1');
  };

  return (
    <Card className="mb-8 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="freemium-onboarding">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Get started — {completed}/{steps.length} done</h3>
              <p className="text-sm text-slate-600">A few quick steps to launch your free site.</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-slate-400 hover:text-slate-600" data-testid="dismiss-onboarding" aria-label="Dismiss">
            <X size={18} />
          </button>
        </div>

        <div className="w-full h-2 bg-indigo-100 rounded-full overflow-hidden mb-5" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>

        <div className="space-y-2">
          {steps.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white border border-slate-100" data-testid={`onboarding-step-${s.key}`}>
              <div className="flex items-center gap-3 min-w-0">
                {s.done ? <CheckCircle2 className="text-emerald-500 flex-shrink-0" size={20} /> : <Circle className="text-slate-300 flex-shrink-0" size={20} />}
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${s.done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{s.label}</p>
                  <p className="text-xs text-slate-500 truncate">{s.desc}</p>
                </div>
              </div>
              {!s.done && (
                <Button size="sm" variant="outline" onClick={s.action} className="flex-shrink-0" data-testid={`onboarding-cta-${s.key}`}>
                  {s.cta}
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between rounded-lg bg-indigo-600 text-white p-4">
          <div>
            <p className="font-semibold">Need pages, newsletters & more?</p>
            <p className="text-sm text-indigo-100">Upgrade to unlock the full builder.</p>
          </div>
          <Button onClick={() => navigate('/settings#billing-management-section')} className="bg-white text-indigo-700 hover:bg-indigo-50" data-testid="onboarding-upgrade-btn">
            Upgrade <ArrowUpRight size={16} className="ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FreemiumOnboarding;
